/** @module App frame: sidebar on desktop, bottom tabs with the foil scan button on phones. */
import { Link, useRouterState } from "@tanstack/react-router";
import {
  Database,
  Ellipsis,
  House,
  Images,
  Layers,
  Library,
  List,
  MessageSquareText,
  Package,
  RefreshCw,
  ScanLine,
  ScanSearch,
  Settings,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { BrandLockup } from "@/components/brand";
import { ToastProvider } from "@/components/ui/overlays";
import { useOpenReviews } from "@/lib/review-count";
import { useAppUpdate } from "@/lib/updates";
import { useSession } from "@/lib/use-session";

interface NavEntry {
  to: string;
  search?: Record<string, string>;
  labelKey: string;
  icon: ReactNode;
  /** Shows the number of open review cases. */
  counts?: boolean;
}

const MAIN: NavEntry[] = [
  { to: "/", labelKey: "nav.dashboard", icon: <House /> },
  { to: "/collection", labelKey: "nav.collection", icon: <Library /> },
  { to: "/scan", labelKey: "nav.scan", icon: <ScanLine /> },
  { to: "/review", labelKey: "nav.reviewQueue", icon: <List />, counts: true },
  { to: "/sets", labelKey: "nav.sets", icon: <Layers /> },
  { to: "/locations", labelKey: "nav.locations", icon: <Package /> },
];

const ADMIN: NavEntry[] = [
  { to: "/admin", search: { tab: "jobs" }, labelKey: "nav.adminJobs", icon: <RefreshCw /> },
  {
    to: "/admin",
    search: { tab: "recognition" },
    labelKey: "nav.adminRecognition",
    icon: <ScanSearch />,
  },
  { to: "/admin", search: { tab: "users" }, labelKey: "nav.adminUsers", icon: <Users /> },
  { to: "/admin", search: { tab: "scans" }, labelKey: "nav.adminScans", icon: <Images /> },
  {
    to: "/admin",
    search: { tab: "feedback" },
    labelKey: "nav.adminFeedback",
    icon: <MessageSquareText />,
  },
  { to: "/admin", search: { tab: "system" }, labelKey: "nav.adminStatus", icon: <Database /> },
];

function useActive() {
  const location = useRouterState({ select: (state) => state.location });
  return (entry: NavEntry): boolean => {
    const path = location.pathname;
    if (entry.search?.tab) {
      const tab = (location.search as { tab?: string }).tab ?? "system";
      return path === "/admin" && tab === entry.search.tab;
    }
    if (entry.to === "/") return path === "/";
    // A card page belongs to the catalogue: "Sets" stays lit there.
    return path.startsWith(entry.to) || (entry.to === "/sets" && path.startsWith("/cards"));
  };
}

function SideLink({ entry, active, count }: { entry: NavEntry; active: boolean; count: number }) {
  const { t } = useTranslation();
  return (
    <Link
      to={entry.to}
      search={entry.search}
      aria-current={active ? "page" : undefined}
      className="nav-item"
    >
      {entry.icon}
      <span className="grow">{t(entry.labelKey)}</span>
      {entry.counts && count > 0 ? <span className="badge badge-count">{count}</span> : null}
    </Link>
  );
}

function Sidebar({ count, admin, name }: { count: number; admin: boolean; name: string }) {
  const { t } = useTranslation();
  const isActive = useActive();
  return (
    <aside className="sticky top-0 hidden h-dvh flex-col print:hidden gap-1 border-r border-(--border) bg-(--surface) px-3 py-5 md:flex">
      <Link to="/" className="px-2 pb-5 text-(--text) no-underline" aria-label={t("app.name")}>
        <BrandLockup />
      </Link>
      <nav aria-label={t("nav.label")} className="flex flex-col gap-1">
        {MAIN.map((entry) => (
          <SideLink key={entry.to} entry={entry} active={isActive(entry)} count={count} />
        ))}
        {admin ? (
          <>
            <span className="t-label nav-section">{t("nav.adminSection")}</span>
            {ADMIN.map((entry) => (
              <SideLink
                key={entry.search?.tab}
                entry={entry}
                active={isActive(entry)}
                count={count}
              />
            ))}
          </>
        ) : null}
      </nav>
      <span className="grow" />
      <SideLink
        entry={{ to: "/settings", labelKey: "nav.settings", icon: <Settings /> }}
        active={isActive({ to: "/settings", labelKey: "", icon: null })}
        count={0}
      />
      <div className="row px-3 pt-2">
        <span className="lang size-7 rounded-full text-[11px]" aria-hidden="true">
          {name.slice(0, 1)}
        </span>
        <span className="t-sm truncate">{name}</span>
      </div>
    </aside>
  );
}

function TabBar({ count }: { count: number }) {
  const { t } = useTranslation();
  const isActive = useActive();
  const tab = (entry: NavEntry) => (
    <Link to={entry.to} aria-current={isActive(entry) ? "page" : undefined} className="tabbar-item">
      {entry.icon}
      {t(entry.labelKey)}
      {entry.counts && count > 0 ? (
        <span className="badge badge-count tabbar-dot">{count}</span>
      ) : null}
    </Link>
  );
  return (
    <nav
      aria-label={t("nav.label")}
      className="fixed inset-x-0 bottom-0 z-20 grid h-(--tabbar-h) print:hidden grid-cols-5 items-center border-t border-(--border) bg-(--surface) px-2 pb-[max(10px,env(safe-area-inset-bottom))] md:hidden"
    >
      {/* Start carries the review badge on phones, where the queue has no tab of its own. */}
      {tab({ to: "/", labelKey: "nav.dashboard", icon: <House />, counts: true })}
      {tab({ to: "/collection", labelKey: "nav.collection", icon: <Library /> })}
      <Link
        to="/scan"
        aria-current={isActive({ to: "/scan", labelKey: "", icon: null }) ? "page" : undefined}
        className="tabbar-item tabbar-center"
      >
        <span className="scan-fab">
          <ScanLine />
        </span>
        {t("nav.scan")}
      </Link>
      {tab({ to: "/sets", labelKey: "nav.sets", icon: <Layers /> })}
      {tab({ to: "/more", labelKey: "nav.more", icon: <Ellipsis /> })}
    </nav>
  );
}

function UpdateBanner() {
  const { t } = useTranslation();
  const update = useAppUpdate();
  if (!update.ready) return null;

  return (
    <div
      role="status"
      className="flex items-center justify-between gap-3 bg-(--accent-soft) px-4 py-2 text-[length:var(--text-sm)] text-(--accent-soft-ink) print:hidden"
    >
      <span>{t("app.updateReady")}</span>
      <button type="button" onClick={update.apply} className="btn btn-primary btn-sm">
        {t("app.updateApply")}
      </button>
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const session = useSession();
  const signedIn = session.status === "signed-in";
  const count = useOpenReviews(signedIn);

  // The login screen gets the whole viewport; no navigation to nowhere.
  if (!signedIn) {
    return (
      <ToastProvider>
        <main className="min-h-dvh">
          <UpdateBanner />
          {children}
        </main>
      </ToastProvider>
    );
  }

  return (
    <ToastProvider>
      <div className="min-h-dvh md:grid md:grid-cols-[var(--sidebar-w)_1fr] print:block">
        <Sidebar
          count={count}
          admin={session.account?.role === "admin"}
          name={session.account?.display_name ?? ""}
        />
        <div className="flex min-h-dvh min-w-0 flex-col">
          <UpdateBanner />
          <main className="grow px-4 pt-4 pb-[calc(var(--tabbar-h)+var(--space-6))] md:px-8 md:py-8 md:pb-10 print:p-0">
            {children}
          </main>
          <TabBar count={count} />
        </div>
      </div>
    </ToastProvider>
  );
}
